<script setup lang="ts">
import { computed, useAttrs } from 'vue';
import { Toast } from 'flowbite-vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

interface Action {
  label: string;
  icon?: string[];
  onClick: () => void;
  class?: string;
}

const props = defineProps<{
  title?: string;
  message: string;
  additionalMessage?: string;
  actions?: Action[];
  type?: string;
}>();

defineEmits(['close']);

const attrs = useAttrs();

// Get toast type from attrs or props or default to empty
const toastType = computed(() => {
  return props.type || attrs.type || 'empty';
});

// Computes the appropriate background and text colors based on toast type
const iconContainerClasses = computed(() => {
  const baseClasses =
    'inline-flex flex-shrink-0 justify-center items-center w-8 h-8 rounded-lg';

  switch (toastType.value) {
    case 'danger':
      return `${baseClasses} text-2xl text-red-500`;
    case 'info':
      return `${baseClasses} text-blue-500 bg-blue-100 dark:bg-blue-800 dark:text-blue-200`;
    case 'success':
      return `${baseClasses} text-green-500 bg-green-100 dark:bg-green-800 dark:text-green-200`;
    case 'warning':
      return `${baseClasses} text-orange-500 bg-orange-100 dark:bg-orange-700 dark:text-orange-200`;
    default:
      return '';
  }
});

// Returns icon component based on toast type
const toastIcon = computed(() => {
  switch (toastType.value) {
    case 'danger':
      return {
        icon: ['fas', 'triangle-exclamation'],
        class: 'animate-pulse h-8 w-8',
      };
    case 'info':
      return {
        icon: ['fas', 'circle-info'],
        class: 'h-5 w-5',
      };
    case 'success':
      return {
        icon: ['fas', 'check'],
        class: 'h-5 w-5',
      };
    case 'warning':
      return {
        icon: ['fas', 'triangle-exclamation'],
        class: 'h-5 w-5',
      };
    default:
      return {
        icon: null,
        class: null,
      };
  }
});
</script>

<template>
  <div data-testid="toast">
    <Toast v-bind="$attrs" data-type="action-toast" @close="$emit('close')">
      <template v-if="toastIcon.icon" #icon>
        <div :class="iconContainerClasses">
          <FontAwesomeIcon :icon="toastIcon.icon" :class="toastIcon.class" />
        </div>
      </template>

      <div class="flex flex-col" :class="{ 'pl-3': toastIcon.icon }">
        <h3 v-if="title" class="text-sm font-semibold leading-5 text-gray-800">
          {{ title }}
        </h3>

        <p
          style="font-size: 0.875rem; line-height: 1.25rem"
          class="text-gray-700"
        >
          {{ message }}
        </p>

        <p
          v-if="additionalMessage"
          style="font-size: 0.875rem; line-height: 1.25rem"
          class="mt-2 text-gray-700"
        >
          {{ additionalMessage }}
        </p>

        <div v-if="actions && actions.length > 0" class="mt-2 flex gap-2">
          <button
            v-for="(action, index) in actions"
            :key="index"
            type="button"
            class="inline-flex items-center rounded-md px-2 py-1 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
            :class="
              action.class ||
              'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
            "
            @click="action.onClick"
          >
            <FontAwesomeIcon
              v-if="action.icon"
              :icon="action.icon"
              class="mr-1"
            />
            {{ action.label }}
          </button>
        </div>
      </div>
    </Toast>
  </div>
</template>

<style lang="scss">
#toast-default[role='alert'] {
  &[data-type='action-toast'] {
    /* box-shadow: none; */

    &[data-toast-type='danger'] {
      border-width: 2px; /* Danger in the standard toast applies a border */
      border-color: rgb(220, 38, 38); /* border-red-600 */
    }

    & > div:first-child {
      background-color: white;
    }
  }
}
</style>
